"use client";

import {
  bannerProperties,
  mediaType,
  promotionalBannerType,
} from "@/apiUtils/constants";
import NoImageSrc from "@/assets/images/no_image.svg";
import {
  MultipleColBanner,
  PromotionalBannerItem,
  PromotionalBannerProperties,
  PromotionalBannerTypes,
  SingleColBanner,
  TranslationData,
} from "@/common/lib/types";
import PromotionalImageCard from "@/components/common/PromotionalBanner/PromotionalImageCard";
import PromotionalVideoCard from "@/components/common/PromotionalBanner/PromotionalVideoCard";
import ImageCard from "@/components/common/imageCard/ImageCard";
import VideoCard from "@/components/common/videoCard/VideoCard";
import Button from "@/components/ui/button/Button";
import { cn } from "@/lib/tailwind";
import { getCloudinaryURL } from "@/utils/cloudinary";
import { buildGradientOverlayCss } from "@/utils/gradientOverlay";
import { getFieldValue, getLangFromLocale } from "@/utils/common";
import { useCallback } from "react";
import { Carousel, Slide } from "@/components/ui/carousel/Carousel";
import AngleLeftIcon from "@/assets/icons/arrow-left.svg?react";
import AngleRightIcon from "@/assets/icons/arrow-right.svg?react";

interface PromoBannerGroup {
  subDescription?: string;
  desktop: Array<SingleColBanner | MultipleColBanner>;
  mobile: Array<SingleColBanner | MultipleColBanner>;
}

interface PromoBannerGroupsProps {
  groups: PromoBannerGroup[];
  locale: string;
  translations?: TranslationData;
  handleClick?: (data: PromotionalBannerItem) => void;
  handleOnVisible?: (isVisible: boolean, data: PromotionalBannerItem) => void;
}

const getButtonStyle = (bgColor?: string, textColor?: string): React.CSSProperties | undefined => {
  if (!bgColor && !textColor) return undefined;
  return {
    ...(bgColor && { backgroundColor: bgColor, borderColor: bgColor }),
    ...(textColor && { color: textColor }),
  };
};

const PromoBannerGroups = ({
  groups,
  locale,
  translations,
  handleClick = () => {},
  handleOnVisible = () => {},
}: PromoBannerGroupsProps) => {
  const lang = getLangFromLocale(locale);

  const renderBanner = useCallback(
    (content: SingleColBanner | MultipleColBanner, index: number) => {
      const bannerType = content?.type;

      // SINGLE COL
      if (
        [
          promotionalBannerType.SINGLE_COL_LARGE_BANNER,
          promotionalBannerType.SINGLE_COL_LARGE_BANNER_MOBILE,
          promotionalBannerType.SINGLE_COL_MEDIUM_BANNER,
          promotionalBannerType.SINGLE_COL_MEDIUM_BANNER_MOBILE,
          promotionalBannerType.SINGLE_COL_SMALL_BANNER,
        ].includes(bannerType)
      ) {
        const properties: PromotionalBannerProperties =
          bannerProperties[bannerType as PromotionalBannerTypes];
        const banner = content as SingleColBanner;
        const isBelow = banner?.contentPosition === "below";
        const hasOverlay = !isBelow && banner?.bannerVariant === "overlay";
        const overlayBackground = hasOverlay ? buildGradientOverlayCss(banner?.overlay) : undefined;
        const buttonStyle = getButtonStyle(banner?.linkBackgroundColor, banner?.linkTextColor);

        const isLinkVariant = banner?.linkVariant === "link";
        const cta = banner?.linkTitle && (
          <Button
            className={cn(
              "font-semibold",
              isLinkVariant
                ? "text-xs md:text-sm"
                : "flex h-[36px] min-w-[120px] items-center justify-center p-0 text-sm md:h-[50px] md:min-w-[170px]",
            )}
            variant={banner?.linkVariant ?? "text"}
            style={buttonStyle}
          >
            {banner?.linkTitle}
          </Button>
        );

        const action = {
          cta,
          title: banner?.title,
          description: banner?.description,
          subDescription: banner?.subDescription,
        };

        return (
          <div key={`singleColWrapper_${index}`} className={cn("h-full", properties?.className)}>
            {isBelow ? (
              banner.mediaType === mediaType.IMAGE ? (
                <PromotionalImageCard
                  url={banner?.link?.url}
                  containerProps={{ className: "h-full w-full", style: { backgroundColor: banner?.color || "#F4F4F4" } }}
                  contentBgColor="transparent"
                  action={action}
                  imageProps={{
                    width: properties?.imageWidth,
                    height: properties?.imageHeight,
                    src: getCloudinaryURL(banner?.media) ?? NoImageSrc,
                    alt: banner?.title ?? "Banner-content",
                    className: "w-full object-cover",
                    style: { height: `${properties?.imageHeight}px` },
                  }}
                  translations={translations}
                  onClick={() => handleClick(banner)}
                  onVisible={(isVisible) => handleOnVisible(isVisible, banner)}
                />
              ) : (
                <PromotionalVideoCard
                  showControls={banner?.showControls}
                  url={banner?.link?.url}
                  containerProps={{ className: "h-full w-full", style: { backgroundColor: banner?.color || "#F4F4F4" } }}
                  contentBgColor="transparent"
                  action={action}
                  videoProps={{
                    src: banner?.media,
                    className: "h-full w-full object-cover",
                    playsInline: true,
                    muted: true,
                    loop: banner?.loop,
                    title: banner?.title,
                    poster: banner?.videoThumbnail ?? NoImageSrc?.src,
                    style: { height: `${properties?.imageHeight}px` },
                  }}
                  translations={translations}
                  onClick={() => handleClick(banner)}
                  onVisible={(isVisible) => handleOnVisible(isVisible, banner)}
                />
              )
            ) : (
              banner.mediaType === mediaType.IMAGE ? (
                <ImageCard
                  url={banner?.link?.url}
                  containerProps={{
                    className: cn("h-full w-full", hasOverlay && "relative"),
                    style: {
                      ...(banner?.color && { backgroundColor: banner.color }),
                      ...(hasOverlay && { background: overlayBackground }),
                    },
                  }}
                  action={action}
                  imageProps={{
                    width: properties?.imageWidth,
                    height: properties?.imageHeight,
                    src: getCloudinaryURL(banner?.media) ?? NoImageSrc,
                    alt: banner?.title ?? "Banner-content",
                    className: "w-full object-cover",
                    style: { height: `${properties?.imageHeight}px` },
                  }}
                  onClick={() => handleClick(banner)}
                  onVisible={(isVisible) => handleOnVisible(isVisible, banner)}
                />
              ) : (
                <VideoCard
                  showControls={banner?.showControls}
                  url={banner?.link?.url}
                  containerProps={{
                    className: cn("h-full w-full", hasOverlay && "relative"),
                    style: {
                      ...(banner?.color && { backgroundColor: banner.color }),
                      ...(hasOverlay && { background: overlayBackground }),
                    },
                  }}
                  action={action}
                  videoProps={{
                    src: banner?.media,
                    className: "h-full w-full object-cover",
                    playsInline: true,
                    muted: true,
                    loop: banner?.loop,
                    title: banner?.title,
                    poster: banner?.videoThumbnail ?? NoImageSrc?.src,
                    style: { height: `${properties?.imageHeight}px` },
                  }}
                  onClick={() => handleClick(banner)}
                  onVisible={(isVisible) => handleOnVisible(isVisible, banner)}
                />
              )
            )}
          </div>
        );
      }

      // MULTI COL
      if (
        [
          promotionalBannerType.TWO_COL_BANNER,
          promotionalBannerType.TWO_COL_BANNER_MOBILE,
          promotionalBannerType.THREE_COL_BANNER,
          promotionalBannerType.MIXED_THREE_COL_BANNER,
          promotionalBannerType.FOUR_COL_BANNER,
        ].includes(bannerType)
      ) {
        return (content as MultipleColBanner)?.items?.map((item, i) => {
          const key = item?.key ?? `${index}_${i}`;
          const type =
            bannerType === promotionalBannerType.MIXED_THREE_COL_BANNER
              ? `${bannerType}${item?.type}`
              : bannerType;
          const properties: PromotionalBannerProperties =
            bannerProperties[type as PromotionalBannerTypes];
          const isBelow = item?.contentPosition === "below";
          const hasOverlay = !isBelow && item?.bannerVariant === "overlay";
          const overlayBackground = hasOverlay ? buildGradientOverlayCss(item?.overlay) : undefined;
          const buttonStyle = getButtonStyle(item?.linkBackgroundColor, item?.linkTextColor);
          const isLinkVariant = item?.linkVariant === "link";

          const cta = item?.linkTitle && (
            <Button
              className={cn(
                "font-semibold",
                isLinkVariant
                  ? "text-xs md:text-sm"
                  : "flex h-[36px] min-w-[120px] items-center justify-center p-0 text-sm md:h-[50px] md:min-w-[170px]",
              )}
              variant={item?.linkVariant ?? "text"}
              style={buttonStyle}
            >
              {item?.linkTitle}
            </Button>
          );

          const action = {
            cta,
            title: item?.title,
            description: item?.description,
            subDescription: item?.subDescription,
          };

          return (
            <div key={`multiColWrapper_${key}`} className={cn("h-full", properties?.className)}>
              {isBelow ? (
                item.mediaType === mediaType.IMAGE ? (
                  <PromotionalImageCard
                    url={item?.link?.url}
                    containerProps={{ className: "h-full w-full", style: { backgroundColor: item?.color || "#F4F4F4" } }}
                    contentBgColor="transparent"
                    imageProps={{
                      alt: item?.title ?? getFieldValue(lang, "Promotional Banner", "لافتة ترويجية") ?? "",
                      src: getCloudinaryURL(item?.media) ?? NoImageSrc,
                      height: properties?.imageHeight,
                      width: properties?.imageWidth,
                      className: "w-full object-cover",
                      style: { height: `${properties?.imageHeight}px` },
                    }}
                    action={action}
                    translations={translations}
                    onClick={() => handleClick(item)}
                    onVisible={(isVisible) => handleOnVisible(isVisible, item)}
                  />
                ) : (
                  <PromotionalVideoCard
                    key={key}
                    url={item?.link?.url}
                    showControls={item?.showControls}
                    containerProps={{ className: "h-full w-full", style: { backgroundColor: item?.color || "#F4F4F4" } }}
                    contentBgColor="transparent"
                    action={action}
                    videoProps={{
                      src: item?.media,
                      className: "w-full object-cover",
                      muted: true,
                      title: item?.title,
                      loop: true,
                      poster: item?.videoThumbnail ?? NoImageSrc?.src,
                      style: { height: `${properties?.imageHeight}px` },
                    }}
                    translations={translations}
                    onClick={() => handleClick(item)}
                    onVisible={(isVisible) => handleOnVisible(isVisible, item)}
                  />
                )
              ) : (
                item.mediaType === mediaType.IMAGE ? (
                  <ImageCard
                    url={item?.link?.url}
                    containerProps={{
                      className: cn("h-full w-full", hasOverlay && "relative"),
                      style: {
                        ...(item?.color && { backgroundColor: item.color }),
                        ...(hasOverlay && { background: overlayBackground }),
                      },
                    }}
                    imageProps={{
                      alt: item?.title ?? getFieldValue(lang, "Promotional Banner", "لافتة ترويجية") ?? "",
                      src: getCloudinaryURL(item?.media) ?? NoImageSrc,
                      height: properties?.imageHeight,
                      width: properties?.imageWidth,
                      className: "w-full object-cover",
                      style: { height: `${properties?.imageHeight}px` },
                    }}
                    action={action}
                    onClick={() => handleClick(item)}
                    onVisible={(isVisible) => handleOnVisible(isVisible, item)}
                  />
                ) : (
                  <VideoCard
                    key={key}
                    url={item?.link?.url}
                    showControls={item?.showControls}
                    containerProps={{
                      className: cn("h-full w-full", hasOverlay && "relative"),
                      style: {
                        ...(item?.color && { backgroundColor: item.color }),
                        ...(hasOverlay && { background: overlayBackground }),
                      },
                    }}
                    action={action}
                    videoProps={{
                      src: item?.media,
                      className: "w-full object-cover",
                      muted: true,
                      title: item?.title,
                      loop: true,
                      poster: item?.videoThumbnail ?? NoImageSrc?.src,
                      style: { height: `${properties?.imageHeight}px` },
                    }}
                    onClick={() => handleClick(item)}
                    onVisible={(isVisible) => handleOnVisible(isVisible, item)}
                  />
                )
              )}
            </div>
          );
        });
      }

      return <></>;
    },
    [locale, translations, handleClick, handleOnVisible, lang],
  );

  const renderCarouselSlides = (items: Array<SingleColBanner | MultipleColBanner>): Slide[] => {
    const slides: Slide[] = [];
    
    items.forEach((item, index) => {
      const rendered = renderBanner(item, index);
      if (!rendered) return;

      // For multi-col banners, they return an array, so we need to handle that
      if (Array.isArray(rendered)) {
        rendered.forEach((banner) => {
          if (banner) {
            slides.push({
              slideClasses: "basis-full",
              content: banner,
            } as Slide);
          }
        });
      } else {
        slides.push({
          slideClasses: "basis-full",
          content: rendered,
        } as Slide);
      }
    });

    return slides;
  };

  if (!groups || !Array.isArray(groups) || groups.length === 0) {
    return null;
  }

  // Render a single group's banners in grid layout
  const renderGroupContent = (group: PromoBannerGroup, isMobile: boolean = false) => {
    const items = isMobile ? group.mobile : group.desktop;

    if (!items || items.length === 0) {
      return null;
    }

    return (
      <div className={cn("container grid grid-cols-12 gap-6 overflow-hidden px-4 md:grid md:px-12", isMobile && "gap-3")}>
        {items.map((item, index) => renderBanner(item, index))}
      </div>
    );
  };

  // carousel slides for groups
  const groupSlides: Slide[] = groups.map((group, groupIndex) => ({
    slideClasses: "basis-full",
    content: (
      <div key={`promoBannerGroup_${groupIndex}`} className="w-full">
        {group.subDescription && (
          <p className="mb-4 text-center text-sm text-gray-600 md:text-base">{group.subDescription}</p>
        )}
        
        {/* Desktop View */}
        <div className="hidden md:block">
          {renderGroupContent(group, false)}
        </div>

        {/* Mobile View */}
        <div className="block md:hidden">
          {renderGroupContent(group, true)}
        </div>
      </div>
    ),
  }));

  return (
    <>
      {groups.length > 1 ? (
        <Carousel
          carouselOptions={{ dragFree: true, loop: false }}
          spacing={0}
          slides={groupSlides}
          controls={{
            left: {
              top: "50%",
              content: (
                <div className="hidden p-2 md:block">
                  <AngleLeftIcon />
                </div>
              ),
            },
            right: {
              top: "50%",
              content: (
                <div className="hidden p-2 md:block">
                  <AngleRightIcon />
                </div>
              ),
            },
          }}
        />
      ) : (
        // Single group - no carousel needed
        groups[0] && (
          <>
            {groups[0].subDescription && (
              <p className="mb-4 text-center text-sm text-gray-600 md:text-base">{groups[0].subDescription}</p>
            )}
            <div className="hidden md:block">
              {renderGroupContent(groups[0], false)}
            </div>
            <div className="block md:hidden">
              {renderGroupContent(groups[0], true)}
            </div>
          </>
        )
      )}
    </>
  );
};

export default PromoBannerGroups;
